import { Checkbox, Chip, FormControlLabel, Grid, InputAdornment, Stack, TextField } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import { t } from 'i18next';
import * as React from 'react';
import Iconify from '../iconify/Iconify';
import TextInput from './TextInput';

const ListItem = styled('li')(({ theme }) => ({
    margin: theme.spacing(0.5),
}));
export default function InputGroupProperty({
    variant = 'outlined',
    name,
    getValue,
    isChecked = false,
    disabled,
    size = 'small',
    readOnly,
    valueChange = [],
    required = true,
    autoFocus = false,
}) {
    const [value, setValue] = React.useState('');
    const [propertyText, setPropertyText] = React.useState('');
    const [arrValue, setArrValue] = React.useState([]);
    const [err, setErr] = React.useState({
        property: false,
        valueProperty: false,
        helperTextProperty: '',
        helperTextValueProperty: '',
    });

    const handleOnChange = (e) => {
        setErr({
            property: false,
            valueProperty: false,
            helperTextProperty: '',
            helperTextValueProperty: '',
        });
        setValue(e.target.value);
    };

    React.useEffect(() => {
        setArrValue(valueChange || []);
    }, [valueChange]);
    const handleAdd = () => {
        const trimmedValue = value.trim();
        const isPropertyEmpty = !propertyText;
        const isValueEmpty = !trimmedValue;

        // Validate inputs
        if (isPropertyEmpty || isValueEmpty) {
            setErr({
                property: isPropertyEmpty,
                valueProperty: isValueEmpty,
                ...(isPropertyEmpty && { helperTextProperty: t('helperTextProperty_text') }),
                ...(isValueEmpty && { helperTextValueProperty: t('helperTextValueProperty') }),
            });
            return;
        }

        const index = arrValue?.findIndex((item) => item.label === propertyText);
        const updatedValue = [...arrValue];

        if (index > -1) {
            // Update existing label
            updatedValue[index] = {
                ...updatedValue[index],
                value: [...updatedValue[index].value, trimmedValue],
            };
        } else {
            // Add new label
            const newItem = {
                label: propertyText,
                id: generateUniqueId(),
                value: [trimmedValue],
                checked: false,
            };
            updatedValue.push(newItem);
        }

        setArrValue(updatedValue || []);
        getValue?.(updatedValue);
        setValue('');
    };

    const handleKeyPress = (event) => {
        if (event.key !== 'Enter') return;

        event.preventDefault();

        const inputValue = event.target.value.trim();

        // Validate inputs
        const isPropertyEmpty = !propertyText;
        const isValueEmpty = !inputValue;

        if (isPropertyEmpty || isValueEmpty) {
            setErr({
                property: isPropertyEmpty,
                valueProperty: isValueEmpty,
                ...(isPropertyEmpty && { helperTextProperty: t('helperTextProperty_text') }),
                ...(isValueEmpty && { helperTextValueProperty: t('helperTextValueProperty') }),
            });
            return;
        }

        const index = arrValue.findIndex((item) => item.label === propertyText);
        const updatedValue = [...arrValue];

        if (index > -1) {
            // Update existing label
            updatedValue[index] = {
                ...updatedValue[index],
                value: [...updatedValue[index].value, inputValue],
            };
        } else {
            // Create new label
            const newItem = {
                label: propertyText,
                id: generateUniqueId(),
                value: [inputValue],
                checked: !!event.target.checked,
            };
            updatedValue.push(newItem);
        }

        setArrValue(updatedValue || []);
        getValue?.(updatedValue);
        setValue('');
    };

    const generateUniqueId = () => {
        const uniqueId = Math.random().toString(36).substr(2, 9);
        return uniqueId;
    };

    const handleClick = (item, index) => {
        setPropertyText(item?.label);
        if (item) {
            arrValue[index] = { ...item, checked: !item.checked };
            setArrValue([...arrValue]);
            getValue && getValue([...arrValue]);
        }
    };

    const handleDelete = (item, index) => {
        arrValue.splice(index, 1);
        setArrValue([...arrValue]);
        getValue && getValue([...arrValue]);
    };

    const handleDeleteValue = (item, index, indexParent) => {
        console.info('You clicked the delete icon.', item, index);
        if (arrValue[indexParent]?.value?.length > 1) arrValue[indexParent]?.value.splice(index, 1);
        else arrValue.splice(indexParent, 1);
        setArrValue([...arrValue]);
        getValue && getValue([...arrValue]);
    };

    return (
        <>
            <Grid item xl={6} md={6} sm={12} style={{ width: '100%' }}>
                <TextInput
                    label={t('name_property')}
                    valueChange={`${propertyText}`}
                    helperText={err.helperTextProperty}
                    disabled={disabled}
                    error={err.property}
                    required={required}
                    getValue={setPropertyText}
                />
            </Grid>
            <Grid item xl={6} md={6} sm={12} style={{ width: '100%', position: 'relative' }}>
                <TextField
                    onKeyDown={handleKeyPress}
                    sx={{ flex: 1 }}
                    size={size}
                    fullWidth
                    error={err.valueProperty}
                    value={value}
                    disabled={disabled}
                    required={required}
                    helperText={err.helperTextValueProperty}
                    name={name}
                    readOnly={readOnly}
                    label={t('enter_group_property')}
                    autoFocus={autoFocus}
                    onChange={handleOnChange}
                    variant={variant}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={handleAdd}
                                    color={err.valueProperty ? 'error' : 'primary'}
                                    sx={{ right: -7 }}
                                    aria-label="directions"
                                >
                                    <Iconify icon="material-symbols:add-circle-outline" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Grid>
            <Grid item xl={12} md={12} sm={12} style={{ width: '100%' }}>
                <Stack direction="row" flexWrap={'wrap'} spacing={0} sx={{ gap: 1, paddingTop: 1 }}>
                    {isChecked
                        ? arrValue?.length > 0 &&
                          arrValue.map((chip, index) => (
                              <Chip
                                  variant="outlined"
                                  key={index}
                                  label={
                                      <FormControlLabel
                                          control={<Checkbox checked={chip.checked} size="small" />}
                                          label={chip.label}
                                          sx={{ fontSize: '14px', color: 'primary' }}
                                      />
                                  }
                                  onClick={() => handleClick(chip, index)}
                                  onDelete={() => handleDelete(chip, index)}
                              />
                          ))
                        : arrValue?.length > 0 &&
                          arrValue.map((chip, index) => (
                              <Stack key={index} sx={{ display: 'flex', flexWrap: 'wrap', width: '100%' }}>
                                  <Chip
                                      variant="filled"
                                      sx={{ width: '100%', borderRadius: '3px' }}
                                      label={chip.label}
                                      onClick={() => handleClick(chip, index)}
                                      onDelete={() => handleDelete(chip, index)}
                                  />

                                  <Paper
                                      sx={{
                                          display: 'flex',
                                          flexWrap: 'wrap',
                                          flexDirection: 'row',
                                          listStyle: 'none',
                                          p: 0.1,
                                          m: 0,
                                          paddingTop: 0.5,
                                      }}
                                      component="ul"
                                  >
                                      {chip?.value?.length > 0 &&
                                          chip.value.map((valueChip, idx) => (
                                              <ListItem key={idx}>
                                                  <Chip
                                                      color="primary"
                                                      variant="outlined"
                                                      sx={{ borderRadius: '3px' }}
                                                      label={valueChip}
                                                      onDelete={() => handleDeleteValue(valueChip, idx, index)}
                                                  />
                                              </ListItem>
                                          ))}
                                  </Paper>
                              </Stack>
                          ))}
                </Stack>
            </Grid>
        </>
    );
}
